import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Group, Stack, Title } from '@mantine/core'
import { SeasonDropdown } from '@shared/components/season-dropdown/season-dropdown'
import { CreditorHistoryTable } from '@shared/components/wool-data/creditor-history-table/creditor-history-table'
import { GetCreditorHistoryGql } from '@/graphql/queries/wool-data/get-creditor-history.graphql'
import { toLocalDate } from '@shared/helpers/format-date/format-date'
import { useAuthenticatedGraphQuery } from '@/hooks/use-authenticated-query'
import { AlertLevel, IconAlert } from '@shared/components/icon-alert/icon-alert'

export function CreditorHistory() {
  const [seasonStartDate, setSeasonStartDate] = useState<Date | undefined>(undefined)
  const [seasonEndDate, setSeasonEndDate] = useState<Date | undefined>(undefined)

  // Get the station record number from the URL
  const { stationRecordNumber } = useParams()

  // Convert Date objects to ISO date strings for GraphQL
  const { fromPaymentDate, toPaymentDate } = useMemo(
    () => ({
      fromPaymentDate: toLocalDate(seasonStartDate),
      toPaymentDate: toLocalDate(seasonEndDate),
    }),
    [seasonStartDate, seasonEndDate]
  )

  // Get the creditor history data
  const { data, isLoading, isError, error } = useAuthenticatedGraphQuery({
    queryKey: ['creditorHistoryData', stationRecordNumber, fromPaymentDate, toPaymentDate],
    gql: GetCreditorHistoryGql,
    queryParams: {
      stationRecordNumber,
      fromPaymentDate,
      toPaymentDate,
    },
    enabled: !!stationRecordNumber && !!fromPaymentDate && !!toPaymentDate,
  })

  if (isError && error.response?.status === 403) {
    return (
      <Stack>
        <Title order={2} data-testid='station-wool-data-creditor-history'>
          Creditor History
        </Title>
        <IconAlert
          title='Restricted Access'
          level={AlertLevel.WARNING}
          message='You do not have permission to view creditor history for this station.'
        />
      </Stack>
    )
  }

  return (
    <Stack>
      <Group>
        <Title order={2} data-testid='station-wool-data-creditor-history' mr='auto'>
          Creditor History
        </Title>
        <SeasonDropdown
          seasonsBeforeCurrent={5}
          seasonsAfterCurrent={1}
          setSeasonStartDate={setSeasonStartDate}
          setSeasonEndDate={setSeasonEndDate}
        />
      </Group>
      <CreditorHistoryTable
        data={data?.listCreditorHistory?.results ?? []}
        sumOfGrossProceedsExGST={data?.listCreditorHistory?.sumOfGrossProceedsExGST ?? null}
        sumOfNetProceedsGSTInclusive={
          data?.listCreditorHistory?.sumOfNetProceedsGSTInclusive ?? null
        }
        sumOfTotalBales={data?.listCreditorHistory?.sumOfTotalBales ?? null}
        sumOfTotalNetKg={data?.listCreditorHistory?.sumOfTotalNetKg ?? null}
        isLoading={isLoading}
        isError={isError}
        reducedView
      />
    </Stack>
  )
}
